import { KeyboardEvent, MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, Maximize2, PanelRightOpen, Printer } from '@digital-twins/design-system/icons'

import { printStudyDoc, StudyDocBody, StudyDocPlotLookup, StudyDocQuote } from '@/components/study-doc'
import { Heading } from '@/components/ui/Heading'

import { artifactDisplayLabel, stripLeadingHeading } from './ThreadRunPane/artifactDisplayLabel'
import { ThreadArtifactItem } from './ThreadRunPane/collectThreadArtifacts'

/** The saved report a message announced (`citations.report` / `done.report`). */
export interface ThreadReportLink {
  publicId: string
  title: string
}

export interface ThreadReportCardProps {
  report: ThreadReportLink
  /** The living artifact, matched by `publicId`; null until the library loads. */
  item: ThreadArtifactItem | null
  /** Opens the artifact in the side pane (drill-in). */
  onExpand: (item: ThreadArtifactItem) => void
  onFullscreen: (item: ThreadArtifactItem) => void
  /** `{{plot:id}}` / `{{quote:id}}` lookups for the preview body. */
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  /** Accessible name of the open action. */
  openLabel?: string
  /** Adds the open-in-side-pane action; off where the pane holds nothing more (Quick Study). */
  hasPaneAction?: boolean
}

/**
 * The preview is a picture of the document, not a second copy of
 * its controls. `inert` keeps chart controls out of the tab order (React 18
 * has no typed prop for it).
 */
function makePreviewInert(node: HTMLDivElement | null) {
  node?.setAttribute('inert', '')
}

/** Time for the full-screen document to mount and rasterize its charts before printing. */
const PAGE_PRINT_DELAY_MS = 800

const PAGE_ACTION_CLASS =
  'rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground'

/** The body up to and including its first chart token. */
function pagePreviewBody(body: string): string {
  const stripped = stripLeadingHeading(body)
  const match = /\{\{plot:[^}]+\}\}/.exec(stripped)
  return match ? stripped.slice(0, match.index + match[0].length) : stripped
}

/**
 * A12/A13: the report inline under the message that announced it, drawn as a
 * page-like preview (headline, first lines, the first chart) that opens full
 * screen on click or Enter/Space. One shape for every report: the Quick Study
 * narrative and the thread report (create report command and skill) share it.
 */
export function ThreadReportCard(props: ThreadReportCardProps) {
  const { report, item, onExpand, onFullscreen, plots, quotes, openLabel, hasPaneAction = false } = props

  const { t } = useTranslation('app')

  const [hasCopied, setHasCopied] = useState(false)

  const title = item ? artifactDisplayLabel(item).headline : report.title

  const onPagePaneClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (item) {
      onExpand(item)
    }
  }

  const onFullscreenClick = () => {
    if (item) {
      onFullscreen(item)
    }
  }

  const onPageCopyClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!item || !navigator.clipboard?.writeText) {
      return
    }
    try {
      await navigator.clipboard.writeText(item.body)
      setHasCopied(true)
      window.setTimeout(() => setHasCopied(false), 1500)
    } catch {
      setHasCopied(false)
    }
  }

  // The inline card shows a preview, so print opens the whole document first;
  // print CSS then picks the full-screen view over the card.
  const onPagePrintClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!item) {
      return
    }
    onFullscreen(item)
    window.setTimeout(() => {
      void printStudyDoc()
    }, PAGE_PRINT_DELAY_MS)
  }

  const onPageExpandClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onFullscreenClick()
  }

  const onPageKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onFullscreenClick()
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={openLabel ?? t('threads.detail.reportCard.fullscreen', { name: title })}
      className="mt-3 w-full cursor-pointer rounded-xl border border-border bg-background px-6 py-5 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`thread-report-card-${report.publicId}`}
      data-study-doc-print-title={item ? item.title || title : undefined}
      onClick={onFullscreenClick}
      onKeyDown={onPageKeyDown}
    >
      {item ? (
        <div className="-mr-2 -mt-1 flex items-center justify-end gap-1">
          {hasPaneAction ? (
            <button
              type="button"
              className={PAGE_ACTION_CLASS}
              data-testid={`thread-report-card-expand-${report.publicId}`}
              aria-label={t('threads.detail.reportCard.expand', { name: title })}
              onClick={onPagePaneClick}
            >
              <PanelRightOpen className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            className={PAGE_ACTION_CLASS}
            data-testid={`thread-report-card-copy-${report.publicId}`}
            aria-label={
              hasCopied
                ? t('threads.detail.rail.copiedNamedAria', { name: title })
                : t('threads.detail.rail.copyNamedAria', { name: title })
            }
            onClick={onPageCopyClick}
          >
            {hasCopied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
          </button>
          <button
            type="button"
            className={PAGE_ACTION_CLASS}
            data-testid={`thread-report-card-print-${report.publicId}`}
            aria-label={t('threads.detail.reportCard.print', { name: title })}
            onClick={onPagePrintClick}
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            className={PAGE_ACTION_CLASS}
            data-testid={`thread-report-card-fullscreen-${report.publicId}`}
            aria-label={t('threads.detail.reportCard.fullscreen', { name: title })}
            onClick={onPageExpandClick}
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}
      {/* Same centered column as the study doc body, so the headline lines up with the text. */}
      <div className="mx-auto mt-2 w-full max-w-[42rem]">
        <Heading level={3} size="lg" weight="medium" className="leading-snug">
          {title}
        </Heading>
      </div>
      {item ? (
        <div
          className="pointer-events-none relative mt-3 max-h-80 overflow-hidden [&_article>section:first-of-type]:!mt-0"
          data-testid={`thread-report-card-page-${report.publicId}`}
          ref={makePreviewInert}
        >
          <StudyDocBody markdown={pagePreviewBody(item.body)} plots={plots} quotes={quotes} plotRegion="plot" />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent"
            aria-hidden
          />
        </div>
      ) : null}
    </div>
  )
}
